/**
 * PR-H — adaptive grid (shader-driven fullscreen quad).
 *
 * The grid is rendered as a single `THREE.Mesh` carrying a fullscreen
 * NDC-space quad. The fragment shader knows the camera's world frustum
 * (passed as uniforms) and computes the world-space position of each
 * fragment, then draws minor grid lines + major grid lines (every 10×
 * minor) using simple `mod()` arithmetic.
 *
 * The grid "spacing" is chosen adaptively from the camera zoom so the
 * minor lines stay roughly 1 logical drawing unit wide on screen
 * regardless of how zoomed-in or zoomed-out the user is. The plan
 * (`docs/plans/2026-05-05_threejs_webgpu_renderer.md` §PR-H) defines:
 *
 *     spacing = 10^floor(log10(1 / cameraZoom))
 *
 * which snaps the minor grid spacing to powers of ten as the camera
 * zoom crosses each decade boundary. Major grid spacing is always
 * `10× minor`.
 *
 * The mesh sits at `renderOrder = -100` so every entity / overlay
 * paints over it. Depth writes are disabled — the grid never occludes
 * scene content even when other meshes share its z=0 plane.
 *
 * # Why a fullscreen quad and not world-space line meshes?
 *
 *   * One draw call regardless of grid extent (vs. potentially
 *     thousands of `LineSegments2` for a covered-by-frustum patch).
 *   * No CPU work on camera change — the shader sees the new uniforms
 *     and re-evaluates per-fragment.
 *   * Anti-aliasing for free via `fwidth()` smoothing in the shader.
 *
 * # Color / sysvar plumbing
 *
 * AutoCAD's `GRIDDISPLAY` controls grid behaviour (adaptive on/off,
 * follow zoom, etc.). We honour the *adaptive* flag implicitly (we
 * always recompute spacing per-frame); the colour comes from a default
 * dark-grey that matches the SVG renderer's `cad-grid-minor` /
 * `cad-grid-major` patterns. PR-I exposes a per-document override
 * once the layer table wiring lands.
 */

import * as THREE from "three";

/**
 * Compute the adaptive minor grid spacing for a given camera zoom.
 *
 *   spacing = 10^floor(log10(1 / cameraZoom))
 *
 * Examples (verified by `gridShader.test.ts`):
 *   - cameraZoom = 0.01 → 1/zoom = 100 → log10 = 2 → spacing = 100
 *   - cameraZoom = 1    → 1/zoom = 1   → log10 = 0 → spacing = 1
 *   - cameraZoom = 100  → 1/zoom = 0.01 → log10 = -2 → spacing = 0.01
 */
export function adaptiveSpacing(cameraZoom: number): number {
  if (!Number.isFinite(cameraZoom) || cameraZoom <= 0) return 1;
  const exponent = Math.floor(Math.log10(1 / cameraZoom));
  return Math.pow(10, exponent);
}

/**
 * Major spacing is always 10× minor (one decade up).
 */
export function majorSpacing(minorSpacing: number): number {
  return minorSpacing * 10;
}

/**
 * Minimal vertex shader: paint a fullscreen NDC quad. We intentionally
 * skip the model/view/projection chain because the quad is positioned
 * directly in clip space — every camera change is reflected through
 * the fragment shader's `uMinX/uMaxX/uMinY/uMaxY` uniforms instead.
 */
export const GRID_VERTEX_SHADER = /* glsl */ `
  varying vec2 vNdc;
  void main() {
    vNdc = position.xy;
    // Emit the fullscreen quad at the FAR plane (NDC z ≈ 1) so opaque model
    // geometry — which is nearer and writes depth — OCCLUDES the grid via the
    // depth test (see createGridMesh: depthTest:true). Without this the grid,
    // being \`transparent:true\`, renders in three.js's transparent pass AFTER all
    // opaque geometry (renderOrder only orders WITHIN that pass), so its dark
    // minor/major lines blended OVER model SEGMENT/LWPOLYLINE bodies at every
    // grid crossing — a fine longitudinal stipple/beading on the lines when
    // magnified. Depth-occluding the grid keeps it a true background layer.
    gl_Position = vec4(position.xy, 0.999999, 1.0);
  }
`;

/**
 * Fragment shader: convert NDC → world via the frustum uniforms, then
 * sample a minor + major grid with anti-aliased lines. Output alpha is
 * proportional to "how close are we to a grid line" measured in
 * pixels (via `fwidth`) so lines stay 1px on screen at any zoom.
 */
export const GRID_FRAGMENT_SHADER = /* glsl */ `
  precision highp float;
  varying vec2 vNdc;
  uniform float uMinX;
  uniform float uMaxX;
  uniform float uMinY;
  uniform float uMaxY;
  uniform float uMinorSpacing;
  uniform float uMajorSpacing;
  uniform vec3 uMinorColor;
  uniform vec3 uMajorColor;
  uniform float uMinorAlpha;
  uniform float uMajorAlpha;

  // Distance in world units from the nearest grid line on a given axis.
  float gridLineDist(float v, float spacing) {
    float m = mod(v + spacing * 0.5, spacing) - spacing * 0.5;
    return abs(m);
  }

  void main() {
    // NDC [-1, 1] → world (offset-world) via the frustum extents.
    float worldX = mix(uMinX, uMaxX, (vNdc.x + 1.0) * 0.5);
    float worldY = mix(uMinY, uMaxY, (vNdc.y + 1.0) * 0.5);

    // Pixel-scale derivative for AA — fwidth gives world units per pixel.
    vec2 px = vec2(fwidth(worldX), fwidth(worldY));
    float pxScale = max(px.x, px.y);

    // Minor grid: nearest-line distance per axis, then take the min.
    float minorDx = gridLineDist(worldX, uMinorSpacing);
    float minorDy = gridLineDist(worldY, uMinorSpacing);
    float minorD = min(minorDx, minorDy);
    float minorBlend = 1.0 - smoothstep(0.0, pxScale * 1.0, minorD);

    // Major grid: nearest-line distance per axis, drawn slightly thicker.
    float majorDx = gridLineDist(worldX, uMajorSpacing);
    float majorDy = gridLineDist(worldY, uMajorSpacing);
    float majorD = min(majorDx, majorDy);
    float majorBlend = 1.0 - smoothstep(0.0, pxScale * 1.5, majorD);

    // Compose: major lines override minor (paint after).
    vec3 color = mix(uMinorColor, uMajorColor, majorBlend);
    float alpha = max(minorBlend * uMinorAlpha, majorBlend * uMajorAlpha);

    if (alpha <= 0.001) discard;
    gl_FragColor = vec4(color, alpha);
  }
`;

/**
 * Uniforms object for the grid material. Mutated in place per frame
 * to reflect camera frustum + adaptive spacing.
 */
export interface GridUniforms {
  uMinX: { value: number };
  uMaxX: { value: number };
  uMinY: { value: number };
  uMaxY: { value: number };
  uMinorSpacing: { value: number };
  uMajorSpacing: { value: number };
  uMinorColor: { value: THREE.Color };
  uMajorColor: { value: THREE.Color };
  uMinorAlpha: { value: number };
  uMajorAlpha: { value: number };
}

/**
 * Sysvar-resolved colours. Mirrors the SVG renderer's CSS
 * `--cad-grid-minor` / `--cad-grid-major` defaults.
 */
export interface GridStyle {
  minorColor: string;
  majorColor: string;
  minorAlpha: number;
  majorAlpha: number;
}

export const DEFAULT_GRID_STYLE: GridStyle = {
  minorColor: "#3a3a40",
  majorColor: "#5c5c66",
  minorAlpha: 0.5,
  majorAlpha: 0.85,
};

/**
 * Build the fullscreen quad mesh + shader material. Returned mesh has
 * `renderOrder = -100` and `frustumCulled = false` so it always
 * paints behind the scene without per-frame visibility checks.
 *
 * Lifetime: caller owns dispose(). The mesh's geometry + material are
 * disposed alongside the scene on unmount.
 */
export function createGridMesh(style: GridStyle = DEFAULT_GRID_STYLE): {
  mesh: THREE.Mesh;
  uniforms: GridUniforms;
} {
  const uniforms: GridUniforms = {
    uMinX: { value: -1 },
    uMaxX: { value: 1 },
    uMinY: { value: -1 },
    uMaxY: { value: 1 },
    uMinorSpacing: { value: 1 },
    uMajorSpacing: { value: 10 },
    uMinorColor: { value: new THREE.Color(style.minorColor) },
    uMajorColor: { value: new THREE.Color(style.majorColor) },
    uMinorAlpha: { value: style.minorAlpha },
    uMajorAlpha: { value: style.majorAlpha },
  };

  const material = new THREE.ShaderMaterial({
    vertexShader: GRID_VERTEX_SHADER,
    fragmentShader: GRID_FRAGMENT_SHADER,
    uniforms: uniforms as unknown as { [k: string]: THREE.IUniform<unknown> },
    // depthTest:true (was false) — with the far-plane quad z (see vertex shader),
    // opaque model geometry occludes the grid so its transparent-pass draw no
    // longer paints grid lines over SEGMENT/LWPOLYLINE bodies (beading fix).
    // depthWrite stays false: the grid must not occlude anything itself.
    depthTest: true,
    depthWrite: false,
    transparent: true,
  });

  // Fullscreen quad: 4 vertices in NDC space, two triangles.
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array([
    -1, -1, 0,
    1, -1, 0,
    1, 1, 0,
    -1, 1, 0,
  ]);
  geometry.setAttribute(
    "position",
    new THREE.BufferAttribute(positions, 3),
  );
  geometry.setIndex([0, 1, 2, 0, 2, 3]);

  const mesh = new THREE.Mesh(geometry, material);
  mesh.frustumCulled = false;
  mesh.renderOrder = -100;
  // Ortho camera sees this in clip space directly; no need to track
  // its position in the scene tree.
  mesh.matrixAutoUpdate = false;
  mesh.userData = { kind: "grid" };
  return { mesh, uniforms };
}

/**
 * Update grid uniforms from the current orthographic camera. Call
 * once per frame inside `useFrame` (only when the camera changes —
 * the demand-frameloop orchestration handles that).
 *
 * Returns the resolved `(minorSpacing, majorSpacing)` so callers can
 * propagate them to the cursor coords readout if they wish.
 */
export function updateGridUniforms(
  uniforms: GridUniforms,
  camera: THREE.OrthographicCamera,
): { minorSpacing: number; majorSpacing: number } {
  const halfW = (camera.right - camera.left) / 2 / camera.zoom;
  const halfH = (camera.top - camera.bottom) / 2 / camera.zoom;
  const cx = camera.position.x;
  const cy = camera.position.y;
  uniforms.uMinX.value = cx - halfW;
  uniforms.uMaxX.value = cx + halfW;
  uniforms.uMinY.value = cy - halfH;
  uniforms.uMaxY.value = cy + halfH;
  const minor = adaptiveSpacing(camera.zoom);
  const major = majorSpacing(minor);
  uniforms.uMinorSpacing.value = minor;
  uniforms.uMajorSpacing.value = major;
  return { minorSpacing: minor, majorSpacing: major };
}
